import { useEffect, useId, useLayoutEffect, useRef, type ReactNode, type RefObject } from "react";
import { Button } from "./Button";

/**
 * Where focus goes when a dialog closes and its opener has gone — a row's button removed by the change the dialog
 * made: the page's h1 (the one PageHeader renders, where RouteFocus and Back to top put it), else <main>. Never <body>.
 */
export function focusFallback(): void {
  (document.querySelector<HTMLElement>("main h1") ?? document.getElementById("main"))?.focus({ preventScroll: true });
}

/**
 * Open a native <dialog> as a modal for as long as the component is mounted, and put focus back where it was when it
 * goes. showModal() gives the focus trap, Esc and the inert page behind; but these dialogs close by UNMOUNTING, and a
 * removed <dialog> returns focus nowhere — it lands on <body> (review of gairm/lms#241). So the opener is recorded
 * before the dialog opens and focused again in the cleanup, after close(); if the opener has gone, `focusFallback`.
 * Esc is turned into `onClose`.
 *
 * While it is open, focus stays inside it (Claude's walk of 2026-09-23, gairm/lms#236): a control that had focus and
 * is disabled while its request runs, or removed by what it did (a Revoke button whose grant is gone), drops focus to
 * <body> with the modal still up. After any change inside the dialog, if focus has been dropped, it goes to the dialog
 * itself — which a screen reader reads by its title — and a refusal then takes it on to its alert (DialogAlert).
 */
export function useModalDialog(ref: RefObject<HTMLDialogElement | null>, onClose: () => void, returnTo?: RefObject<HTMLElement | null>): void {
  // The latest onClose, without re-opening the dialog each time the caller passes a new function.
  const close = useRef(onClose);
  useLayoutEffect(() => {
    close.current = onClose;
  });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Opened from a keyboard shortcut there is no opener in focus; `returnTo` names the control that stands for it.
    const opener = document.activeElement instanceof HTMLElement && document.activeElement !== document.body ? document.activeElement : (returnTo?.current ?? null);
    if (!el.open) el.showModal?.();
    const onCancel = (e: Event) => {
      e.preventDefault();
      close.current();
    };
    el.addEventListener("cancel", onCancel);
    // Focus fix-up happens when the page next renders, not when the DOM changes, so the check waits a frame.
    let frame = 0;
    const keepFocus = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const active = document.activeElement;
        // Only when it was dropped: focus that moved on to a dialog opened from this one (the panel's forms) stays there.
        if (el.open && (!active || active === document.body)) el.focus({ preventScroll: true });
      });
    };
    const watch = typeof MutationObserver === "undefined" ? null : new MutationObserver(keepFocus);
    watch?.observe(el, { subtree: true, childList: true, attributes: true, attributeFilter: ["disabled", "hidden"] });
    el.addEventListener("focusout", keepFocus);
    return () => {
      watch?.disconnect();
      cancelAnimationFrame(frame);
      el.removeEventListener("focusout", keepFocus);
      el.removeEventListener("cancel", onCancel);
      if (el.open) el.close?.();
      // The opener may itself have gone (a row re-rendered, the button removed by what the dialog did).
      if (opener?.isConnected) opener.focus({ preventScroll: true });
      else focusFallback();
    };
  }, [ref, returnTo]);
}

/**
 * A modal on the native <dialog>, labelled by its title; focus returns to the opener when it closes
 * (`useModalDialog`).
 *
 * Focus goes to the element marked `data-autofocus`, else the first field or button inside — the native choice, which
 * lands on whatever is first even when a form means typing to start further down (the support template's pitfall 1:
 * the cursor in the page name, not the description). When the content changes place — a form replaced by its
 * confirmation, so the Send button that had focus is gone — `focusKey` changes and the focus is placed again, or it
 * would fall out of the modal. `closeFocus` sends it to the close button, the one thing left to do on a
 * confirmation, and `closeDescribedBy` names what that button closes on, so the confirmation is read with it.
 */
export function Dialog({
  title,
  children,
  onClose,
  closeLabel = "Cancel",
  wide = false,
  focusKey,
  closeFocus = false,
  closeDescribedBy,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  closeLabel?: string;
  /** A form with two columns: 36rem instead of 28rem, still within the screen. */
  wide?: boolean;
  focusKey?: string;
  closeFocus?: boolean;
  closeDescribedBy?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useModalDialog(ref, onClose);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const target = closeFocus ? el.querySelector<HTMLElement>("[data-dialog-close]") : (el.querySelector<HTMLElement>("[data-autofocus]") ?? el.querySelector<HTMLElement>("input, select, textarea, button:not([data-dialog-close])"));
    target?.focus();
  }, [focusKey, closeFocus]);
  return (
    <dialog ref={ref} tabIndex={-1} aria-labelledby={titleId} className={`m-auto ${wide ? "w-[min(36rem,calc(100vw-2rem))]" : "w-[min(28rem,calc(100vw-2rem))]"} rounded-xl border border-line bg-surface p-5 text-ink shadow-xl backdrop:bg-black/40`}>
      <h2 id={titleId} className="text-lg font-semibold">
        {title}
      </h2>
      <div className="mt-3">{children}</div>
      <div className="mt-4 flex justify-end">
        <Button data-dialog-close onClick={onClose} aria-describedby={closeDescribedBy}>
          {closeLabel}
        </Button>
      </div>
    </dialog>
  );
}
